import { useEffect, useState, useCallback, useMemo } from 'react';
import { IconCopy } from '@tabler/icons-react';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';

import { get } from '@network/api';
import TimeCountdown from './TimeCountdown';
import AccordionItem from './Accordion';
import HeaderResult from '@components/Result/HeaderResult';
import { RenderBankLogoName } from '@components/RenderBankPaymentMethod';
import { SettingsProps, DataDetailCheckout } from '@pages/types';
import { useGenerateNewSession } from '@pages/hooks/useGenerateNewSession';

const MESSAGES = {
  FRAUD_WARNING: 'Protect Yourself Against Fraud',
  FRAUD_DESCRIPTION:
    "Ensure the merchant's name, Payment amount and details are correct Always check before proceeding with payment",
  CHECK_STATUS: 'CHECK STATUS',
  INVALID_VA: 'Invalid VA Number',
  COPY_SUCCESS: 'Copied',
  COPY_FAILED: 'Failed to copy',
  UNKNOWN_ERROR: 'Terjadi kesalahan yang tidak diketahui',
} as const;

interface GuideInfoData {
  payment_channel: string;
  payment_instruction: string;
}

interface ApiError {
  detail: string;
  status_code: number;
}

interface BankData {
  bank_name: string;
  payment_guide_detail: GuideInfoData[];
}

interface VirtualAccountResultProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
  merchantName?: string;
  onPaymentSelected?: () => void;
  onGoToOverview?: () => void;
  onRefreshData?: () => void;
}

interface VAInfoProps {
  vaNumber?: string;
  merchantName?: string;
  bank?: string;
  onCopy: (va: string) => void;
}

const VAInfo: React.FC<VAInfoProps> = ({ vaNumber, bank, onCopy }) => (
  <div className="my-4 flex flex-col gap-4">
    <div>
      <RenderBankLogoName
        title={bank || 'Not Found'}
        width="w-[120px]"
        height="h-[50px]"
      />
    </div>

    <div className="flex flex-row items-start justify-between">
      <div className="flex w-full flex-col gap-1">
        <span className="text-base">Virtual Account Number</span>
        <div className="flex flex-row">
          <div className="w-full flex-1 rounded-lg border border-gray-300 bg-gray-50 px-4 py-2">
            <span className="text-2xl font-semibold">{vaNumber || '-'}</span>
          </div>
          <button
            onClick={() => vaNumber && onCopy(vaNumber)}
            className="ml-2 flex w-max cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 transition-colors hover:bg-gray-100"
          >
            <IconCopy className="h-5 w-5 text-gray-700" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

interface LoadingSkeletonProps {
  count?: number;
}

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ count = 5 }) => (
  <div className="flex items-center justify-center">
    <div className="flex w-full flex-col gap-2">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="mx-auto w-full">
          <Skeleton className="skeleton" height={30} />
        </div>
      ))}
    </div>
  </div>
);

const VirtualAccountResult: React.FC<VirtualAccountResultProps> = ({
  data,
  onRefreshData,
  // settings,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [tabData, setTabData] = useState<GuideInfoData[]>([]);
  const { mutate: generateNewSession, isPending: isGeneratingSession } =
    useGenerateNewSession(onRefreshData);

  const {
    data: dataInfo,
    isLoading,
    error,
  } = useQuery<{ data: BankData[] }, ApiError>({
    queryKey: ['getPaymentInfo'],
    queryFn: () => get(`/checkout/v1/payment/info`),
    retry: false,
  });

  const onCopyVa = useCallback(async (vaNumber: string) => {
    if (!vaNumber) {
      toast.error(MESSAGES.INVALID_VA);
      return;
    }

    try {
      await navigator.clipboard.writeText(vaNumber);
      toast.success(`${MESSAGES.COPY_SUCCESS} ${vaNumber}`);
    } catch {
      toast.error(MESSAGES.COPY_FAILED);
    }
  }, []);

  useEffect(() => {
    if (data?.payment?.va?.bank && dataInfo?.data) {
      const bankName = data.payment.va.bank.split('_')[0].toLowerCase();
      const matchingBank = dataInfo.data.find(
        (bank) => bank.bank_name.toLowerCase() === bankName
      );

      if (matchingBank?.payment_guide_detail) {
        setTabData(matchingBank.payment_guide_detail);
      }
    }
  }, [data?.payment?.va?.bank, dataInfo]);

  useEffect(() => {
    if (error) {
      toast.error(error.detail || MESSAGES.UNKNOWN_ERROR);
    }
  }, [error]);

  // Helper function to parse instruction and extract number
  const parseInstruction = (instruction: string) => {
    const match = instruction.match(/^(\d+)\.\s*(.*)/);
    if (match) {
      return {
        number: match[1],
        text: match[2],
      };
    }
    return {
      number: '',
      text: instruction.trim(),
    };
  };

  const tabsConfig = useMemo(
    () =>
      tabData.map((guide, index) => ({
        id: index,
        name: guide.payment_channel,
        content: (
          <div className="flex flex-col gap-4">
            {guide.payment_instruction.split('\n').map((instruction, idx) => {
              const { number, text } = parseInstruction(instruction.trim());
              return (
                <div key={idx} className="flex items-center gap-3">
                  {number && (
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm font-semibold">
                      {number}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ),
      })),
    [tabData]
  );

  return (
    <div className="flex flex-col gap-4">
      <HeaderResult
        title={`Bank Transfer – Bank ${data?.payment?.va?.bank.split('_')[0].toLowerCase()}`}
        subtitle="Use the details below to complete your payment before the expiry time"
      />

      <TimeCountdown
        expirationTime={data?.payment?.expired_at}
        sessionLabel="Pay within 24 hours"
        invoiceExpirationTime={data?.payment_link_expiration}
        onGenerateNewSession={() => generateNewSession()}
        isGeneratingSession={isGeneratingSession}
      />
      <VAInfo
        vaNumber={data?.payment?.va?.va_number}
        bank={data?.payment?.va?.bank}
        onCopy={onCopyVa}
      />

      <div className="flex flex-col gap-1">
        <span className="text-lg font-semibold capitalize">
          Payment Instruction
        </span>
      </div>

      {/* Info Instruction */}
      <div>
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <div className="flex flex-col gap-1">
            {tabsConfig.map((tab) => (
              <AccordionItem
                key={tab.id}
                title={tab.name}
                content={tab.content}
                isOpen={activeTab === tab.id}
                onToggle={() =>
                  setActiveTab(activeTab === tab.id ? -1 : tab.id)
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VirtualAccountResult;
